import { onUnmounted, ref, toValue, watch, type MaybeRef, type Ref } from 'vue'

import { getRealtimeClient } from '../client'
import type { RealtimeError, RealtimeFrame } from '../types'

import { useRealtimeConnectionState } from './useRealtimeConnectionState'

export interface UseRealtimeSubscriptionOptions<TData = unknown> {
  onMessage: (frame: RealtimeFrame<TData>) => void
  onError?: (err: RealtimeError) => void
}

export interface UseRealtimeSubscriptionReturn {
  isConnected: Ref<boolean>
  isSubscribed: Ref<boolean>
  connectionState: Readonly<Ref<'connected' | 'reconnecting' | 'degraded' | 'failed'>>
  unsubscribe: () => void
}

export function useRealtimeSubscription<TData = unknown>(
  channel: MaybeRef<string>,
  options: UseRealtimeSubscriptionOptions<TData>,
): UseRealtimeSubscriptionReturn {
  const isConnected = ref(false)
  const isSubscribed = ref(false)
  const { state: connectionState } = useRealtimeConnectionState()

  let activeSub: ReturnType<NonNullable<Awaited<ReturnType<typeof getRealtimeClient>>>['newSubscription']> | null = null
  let disposed = false

  const detach = (): void => {
    if (activeSub === null) return
    try {
      activeSub.unsubscribe()
      activeSub.removeAllListeners()
    } catch {
      // swallow — unsubscribe on disposed client is a no-op
    }
    activeSub = null
    isSubscribed.value = false
  }

  const attach = async (channelName: string): Promise<void> => {
    detach()
    if (channelName === '') return

    const client = await getRealtimeClient()
    if (client === null || disposed) return

    let sub = client.getSubscription(channelName)
    if (sub === null) {
      sub = client.newSubscription(channelName)
    }

    sub.on('publication', (ctx) => {
      const data = ctx.data as RealtimeFrame<TData>
      options.onMessage(data)
    })

    sub.on('subscribed', () => {
      isConnected.value = true
      isSubscribed.value = true
    })

    sub.on('unsubscribed', () => {
      isSubscribed.value = false
    })

    sub.on('error', (ctx) => {
      options.onError?.({
        kind: 'subscribe',
        code: ctx.error?.code,
        reason: ctx.error?.message,
      })
    })

    activeSub = sub
    sub.subscribe()
  }

  watch(
    () => toValue(channel),
    (next) => {
      void attach(next)
    },
    { immediate: true },
  )

  const unsubscribe = (): void => {
    disposed = true
    detach()
  }

  onUnmounted(unsubscribe)

  return {
    isConnected,
    isSubscribed,
    connectionState,
    unsubscribe,
  }
}
